'use client'

import { ChevronDown, ChevronRight, DownloadCloud02 } from '@untitledui/icons'
import { useRouter } from 'next/navigation'
import type { FC, ReactNode } from 'react'
import { useState } from 'react'
import { Cell as AriaCell, Row as AriaRow, type Key, type SortDescriptor } from 'react-aria-components'

import { ConfirmDialog } from '@/components/shared/table/ConfirmDialog'
import { ActionsCell } from '@/components/shared/table/columns/ActionsCell'
import { LinkCell } from '@/components/shared/table/columns/LinkCell'
import { LoadingIndicator } from '@/components/shared/ui/application/loading-indicator/loading-indicator'
import { PaginationPageDefault } from '@/components/shared/ui/application/pagination/pagination'
import { Table, TableCard } from '@/components/shared/ui/application/table/table'
import { Badge } from '@/components/shared/ui/base/badges/badges'
import { Button } from '@/components/shared/ui/base/buttons/button'
import { Select } from '@/components/shared/ui/base/select/select'
import { cx } from '@/utils/cx'

export interface DataTableColumn<T> {
  id: string
  header: ReactNode
  cell: (row: T) => ReactNode
  isRowHeader?: boolean
  sortable?: boolean
  align?: 'left' | 'right'
  className?: string
  headerClassName?: string
  width?: string
  /**
   * Platte-tekstwaarde van deze kolom voor de CSV-export. Cellen bevatten vaak JSX
   * (badges, avatars); lever hier de exporteerbare tekst. Kolommen zonder zinvolle
   * tekst (bijv. pure actie-kolommen) laten dit weg of geven een lege string terug.
   */
  csvValue?: (row: T) => string
  /**
   * Splitst één UI-kolom uit naar meerdere CSV-kolommen (bijv. een cel met twee
   * bedragen → "Totaal verantwoord" + "Totaal gedeclareerd"). Heeft voorrang boven
   * csvValue wanneer gezet. Elke entry levert een eigen kolomkop + waarde.
   */
  csvColumns?: { header: string; value: (row: T) => string }[]
}

export interface DataTableConfirm {
  title: string
  description: string
  confirmLabel?: string
  cancelLabel?: string
  destructive?: boolean
}

export interface DataTableRowAction {
  label: string
  icon: FC<{ className?: string }>
  onAction: () => void | Promise<void>
  destructive?: boolean
  confirm?: DataTableConfirm
}

export interface DataTableBulkAction {
  label: string
  icon?: FC<{ className?: string }>
  onAction: () => void | Promise<void>
  destructive?: boolean
  confirm?: DataTableConfirm | ((count: number) => DataTableConfirm)
}

export interface DataTablePagination {
  page: number
  perPage: number
  totalItems: number
  onPageChange: (page: number) => void
  perPageOptions?: number[]
  onPerPageChange?: (perPage: number) => void
}

export interface DataTableProps<T> {
  data: T[]
  columns: DataTableColumn<T>[]
  /** Ids of the columns to render, in the order given by `columns`. Defaults to all columns. */
  visibleColumns?: string[]
  rowKey: (row: T) => string

  pagination: DataTablePagination

  /** Optional card title, rendered with a result-count badge. */
  title?: string
  /** Extra content rendered above the table card (e.g. a filter bar). */
  filterBar?: ReactNode

  isLoading?: boolean
  emptyState?: ReactNode

  /**
   * CSV-export. De knop staat altijd in de header. Standaard exporteert de tabel
   * zijn eigen (al gefilterde) `data`. Voor server-side gepagineerde tabellen levert
   * de pagina hier een functie die de VOLLEDIGE gefilterde set ophaalt, zodat de
   * export niet beperkt is tot de zichtbare pagina.
   */
  exportRows?: () => T[] | Promise<T[]>
  /** Bestandsnaam zonder extensie; standaard `<title>-<YYYY-MM-DD>`. */
  csvFileName?: string
  /** Zet de CSV-export uit (zeldzaam; standaard aan). */
  disableCsvExport?: boolean

  selectable?: boolean
  selectedKeys?: string[]
  onSelectionChange?: (keys: string[]) => void

  sortBy?: string
  sortDir?: 'asc' | 'desc'
  onSortChange?: (sortBy: string, sortDir: 'asc' | 'desc') => void

  rowHref?: (row: T) => string | undefined

  /**
   * Optionele callback wanneer op een rij wordt geklikt (opent bijv. een inline
   * detailweergave via lokale state i.p.v. een URL). Puur additief: zonder deze
   * prop verandert er niets aan het bestaande rij-gedrag. Wordt genegeerd voor
   * rijen waarvoor `rowHref` een URL teruggeeft (navigatie heeft dan voorrang).
   */
  onRowClick?: (row: T) => void

  /** Inline icon actions rendered in a trailing column per row. */
  rowActions?: (row: T) => DataTableRowAction[]
  /** Actions shown in a toolbar above the table when rows are selected. */
  bulkActions?: DataTableBulkAction[]

  /**
   * When provided, enables expandable rows: a leading chevron toggles a full-width detail row
   * rendering this content beneath the row. Opt-in — without it the table behaves exactly as before.
   */
  renderRowDetail?: (row: T) => ReactNode
  /** Whether a given row can be expanded. Defaults to true for every row. */
  isRowExpandable?: (row: T) => boolean
  /** Row keys expanded on first render. */
  defaultExpandedKeys?: string[]
  /** Accordion mode: expanding a row collapses any other expanded row. */
  singleExpand?: boolean
}

export function DataTable<T extends object>({
  data,
  columns,
  visibleColumns,
  rowKey,
  pagination,
  title,
  filterBar,
  isLoading = false,
  emptyState,
  exportRows,
  csvFileName,
  disableCsvExport = false,
  selectable = false,
  selectedKeys,
  onSelectionChange,
  sortBy,
  sortDir,
  onSortChange,
  rowHref,
  onRowClick,
  rowActions,
  bulkActions,
  renderRowDetail,
  isRowExpandable,
  defaultExpandedKeys,
  singleExpand = false,
}: DataTableProps<T>) {
  const router = useRouter()
  const [isExporting, setIsExporting] = useState(false)

  const [pending, setPending] = useState<{ confirm: DataTableConfirm; onAction: () => void | Promise<void> } | null>(
    null
  )
  const [isConfirming, setIsConfirming] = useState(false)
  const [expandedKeys, setExpandedKeys] = useState<Set<string>>(() => new Set(defaultExpandedKeys ?? []))

  const isExpandable = Boolean(renderRowDetail)
  const canExpandRow = (row: T) => (isRowExpandable ? isRowExpandable(row) : true)

  const toggleExpanded = (key: string) =>
    setExpandedKeys((prev) => {
      const isOpen = prev.has(key)
      if (singleExpand) return isOpen ? new Set() : new Set([key])
      const next = new Set(prev)
      if (isOpen) next.delete(key)
      else next.add(key)
      return next
    })

  const runAction = (onAction: () => void | Promise<void>, confirm?: DataTableConfirm) => {
    if (confirm) {
      setPending({ confirm, onAction })
      return
    }
    void onAction()
  }

  const handleConfirm = async () => {
    if (!pending) return
    setIsConfirming(true)
    try {
      await pending.onAction()
      setPending(null)
    } finally {
      setIsConfirming(false)
    }
  }

  const selectionCount = selectedKeys?.length ?? 0

  const baseColumns = visibleColumns ? columns.filter((col) => visibleColumns.includes(col.id)) : columns

  // CSV-export: altijd beschikbaar tenzij expliciet uitgezet. Respecteert de
  // zichtbare kolommen (met csvValue) en de gefilterde data. Server-side tabellen
  // leveren exportRows() voor de volledige gefilterde set; anders de eigen data.
  // Bouw een platte lijst van CSV-kolommen: een kolom met `csvColumns` splitst uit
  // naar meerdere kolommen; anders levert `csvValue` één kolom.
  const csvFields = baseColumns.flatMap((col) => {
    if (col.csvColumns && col.csvColumns.length > 0) {
      return col.csvColumns.map((sub) => ({ header: sub.header, value: sub.value }))
    }
    if (typeof col.csvValue === 'function') {
      return [{ header: String(col.header ?? col.id), value: col.csvValue }]
    }
    return []
  })
  const canExportCsv = !disableCsvExport && csvFields.length > 0

  const escapeCsv = (value: string) => {
    // Formula-injection preventie: prefix cellen die met =,+,-,@ beginnen.
    const guarded = /^[=+\-@]/.test(value) ? `'${value}` : value
    if (/[",\n;]/.test(guarded)) {
      return `"${guarded.replace(/"/g, '""')}"`
    }
    return guarded
  }

  const handleCsvExport = async () => {
    if (isExporting) return
    setIsExporting(true)
    try {
      const rows = exportRows ? await exportRows() : data
      const header = csvFields.map((field) => escapeCsv(field.header)).join(',')
      const body = rows.map((row) => csvFields.map((field) => escapeCsv(field.value(row) ?? '')).join(','))
      const csv = [header, ...body].join('\n')

      const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' })
      const date = new Date().toISOString().slice(0, 10)
      const base = csvFileName ?? `${(title ?? 'export').toLowerCase().replace(/\s+/g, '-')}-${date}`
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `${base}.csv`
      document.body.appendChild(a)
      a.click()
      a.remove()
      URL.revokeObjectURL(url)
    } finally {
      setIsExporting(false)
    }
  }

  const expandColumn: DataTableColumn<T> = {
    id: '__expand__',
    header: <span className="sr-only">Details</span>,
    width: '40',
    isRowHeader: false,
    cell: (row: T) => {
      if (!canExpandRow(row)) return null
      const expanded = expandedKeys.has(rowKey(row))
      const Icon = expanded ? ChevronDown : ChevronRight
      return (
        <button
          type="button"
          aria-label={expanded ? 'Details sluiten' : 'Details tonen'}
          aria-expanded={expanded}
          onClick={(e) => {
            e.stopPropagation()
            toggleExpanded(rowKey(row))
          }}
          className="text-fg-quaternary hover:text-fg-quaternary_hover flex size-6 items-center justify-center rounded-md transition-colors"
        >
          <Icon className="size-4" />
        </button>
      )
    },
  }

  const columnsWithActions: DataTableColumn<T>[] = rowActions
    ? [
        ...baseColumns,
        {
          id: '__rowActions__',
          header: <span className="sr-only">Acties</span>,
          align: 'right',
          cell: (row: T) => (
            <ActionsCell>
              {rowActions(row).map((action) => (
                <LinkCell
                  key={action.label}
                  color="link-gray"
                  size="xs"
                  aria-label={action.label}
                  iconLeading={action.icon}
                  className={action.destructive ? 'hover:*:data-icon:text-fg-error-primary' : undefined}
                  onClick={() =>
                    runAction(
                      action.onAction,
                      action.confirm ? { destructive: action.destructive, ...action.confirm } : undefined
                    )
                  }
                />
              ))}
            </ActionsCell>
          ),
        },
      ]
    : baseColumns

  const renderedColumns: DataTableColumn<T>[] = isExpandable
    ? [expandColumn, ...columnsWithActions]
    : columnsWithActions

  const selectionMode = selectable ? 'multiple' : 'none'
  const selectedSet = new Set<Key>(selectedKeys ?? [])

  // React Aria requires at least one row-header column; fall back to the first real data column
  // (never the synthetic expand/actions columns).
  const hasExplicitRowHeader = renderedColumns.some((col) => col.isRowHeader)
  const firstRowHeaderCandidateId = renderedColumns.find(
    (col) => col.id !== '__expand__' && col.id !== '__rowActions__'
  )?.id

  const totalGridColumns = renderedColumns.length + (selectable ? 1 : 0)
  const detailKeys = isExpandable
    ? data.filter((row) => expandedKeys.has(rowKey(row)) && canExpandRow(row)).map((row) => `${rowKey(row)}__detail`)
    : []
  const disabledKeys = detailKeys.length > 0 ? new Set<Key>(detailKeys) : undefined

  const totalPages = Math.max(1, Math.ceil(pagination.totalItems / pagination.perPage))
  const perPageOptions = pagination.perPageOptions ?? [10, 25, 50, 100]
  const badgeLabel = `${pagination.totalItems} ${pagination.totalItems === 1 ? 'resultaat' : 'resultaten'}`

  const sortDescriptor: SortDescriptor | undefined = sortBy
    ? { column: sortBy, direction: sortDir === 'asc' ? 'ascending' : 'descending' }
    : undefined

  return (
    <div className="flex flex-col gap-6">
      {filterBar}

      {bulkActions && bulkActions.length > 0 && selectionCount > 0 && (
        <div className="flex flex-wrap items-center justify-end gap-3">
          {bulkActions.map((action) => {
            const confirm = typeof action.confirm === 'function' ? action.confirm(selectionCount) : action.confirm
            return (
              <Button
                key={action.label}
                size="sm"
                color={action.destructive ? 'primary-destructive' : 'secondary'}
                iconLeading={action.icon}
                onClick={() =>
                  runAction(action.onAction, confirm ? { destructive: action.destructive, ...confirm } : undefined)
                }
              >
                {`${action.label} (${selectionCount})`}
              </Button>
            )
          })}
        </div>
      )}

      <TableCard.Root>
        {(title || canExportCsv) && (
          <TableCard.Header
            title={title ?? ''}
            badge={
              title ? (
                <Badge color="gray" size="sm">
                  {badgeLabel}
                </Badge>
              ) : undefined
            }
            contentTrailing={
              <div className="flex items-center gap-2">
                {canExportCsv && (
                  <Button
                    size="sm"
                    color="secondary"
                    iconLeading={DownloadCloud02}
                    onClick={handleCsvExport}
                    isLoading={isExporting}
                    isDisabled={isExporting}
                  >
                    CSV
                  </Button>
                )}
                {pagination.onPerPageChange && (
                  <div className="flex items-center gap-2">
                    <span className="text-tertiary text-sm whitespace-nowrap">Rijen per pagina</span>
                    <Select
                      aria-label="Rijen per pagina"
                      size="sm"
                      selectedKey={String(pagination.perPage)}
                      onSelectionChange={(key) => pagination.onPerPageChange?.(Number(key))}
                      items={perPageOptions.map((n) => ({ id: String(n), label: String(n) }))}
                      className="w-20"
                    >
                      {(item) => <Select.Item id={item.id} label={item.label} />}
                    </Select>
                  </div>
                )}
              </div>
            }
          />
        )}

        <div className="relative">
          <Table
            aria-label={title ?? 'Data table'}
            selectionMode={selectionMode}
            selectedKeys={selectedSet}
            disabledKeys={disabledKeys}
            disabledBehavior="selection"
            onSelectionChange={(keys) => {
              if (!onSelectionChange) return
              if (keys === 'all') onSelectionChange(data.map(rowKey))
              else onSelectionChange(Array.from(keys as Set<Key>).map(String))
            }}
            sortDescriptor={sortDescriptor}
            onSortChange={(desc) => {
              if (!onSortChange || !desc.column) return
              onSortChange(String(desc.column), desc.direction === 'ascending' ? 'asc' : 'desc')
            }}
            onRowAction={(key) => {
              const row = data.find((r) => rowKey(r) === String(key))
              if (!row) return
              const href = rowHref?.(row)
              // Navigatie (href) heeft voorrang; anders de optionele klik-callback.
              if (href) {
                router.push(href)
              } else {
                onRowClick?.(row)
              }
            }}
          >
            <Table.Header>
              {renderedColumns.map((col) => {
                const headerIsString = typeof col.header === 'string'
                return (
                  <Table.Head
                    key={col.id}
                    id={col.id}
                    isRowHeader={col.isRowHeader ?? (!hasExplicitRowHeader && col.id === firstRowHeaderCandidateId)}
                    allowsSorting={col.sortable}
                    width={col.width as any}
                    label={headerIsString ? (col.header as string) : undefined}
                    className={cx(col.align === 'right' && 'text-right', col.headerClassName)}
                  >
                    {headerIsString ? null : col.header}
                  </Table.Head>
                )
              })}
            </Table.Header>

            <Table.Body
              items={isExpandable ? undefined : isLoading ? [] : data}
              dependencies={[renderedColumns]}
              renderEmptyState={() =>
                isLoading ? (
                  <div className="flex h-[500px] items-center justify-center">
                    <LoadingIndicator type="line-spinner" size="sm" />
                  </div>
                ) : (
                  <div className="text-tertiary flex h-[500px] items-center justify-center px-6 py-12 text-center text-sm">
                    {emptyState ?? 'Geen resultaten gevonden.'}
                  </div>
                )
              }
            >
              {isExpandable
                ? (isLoading ? [] : data).flatMap((row) => {
                    const key = rowKey(row)
                    const masterRow = (
                      <Table.Row key={key} id={key} className={cx((rowHref?.(row) || onRowClick) && 'cursor-pointer')}>
                        {renderedColumns.map((col) => (
                          <Table.Cell
                            key={col.id}
                            className={cx('text-xs', col.align === 'right' && 'text-right', col.className)}
                          >
                            {col.cell(row)}
                          </Table.Cell>
                        ))}
                      </Table.Row>
                    )

                    if (!expandedKeys.has(key) || !canExpandRow(row)) return [masterRow]

                    const detailRow = (
                      <AriaRow key={`${key}__detail`} id={`${key}__detail`} className="bg-secondary/40">
                        <AriaCell colSpan={totalGridColumns} className="p-0">
                          <div className="border-secondary border-t px-6 py-4 pl-16">{renderRowDetail!(row)}</div>
                        </AriaCell>
                      </AriaRow>
                    )

                    return [masterRow, detailRow]
                  })
                : (row: T) => (
                    <Table.Row id={rowKey(row)} className={cx((rowHref?.(row) || onRowClick) && 'cursor-pointer')}>
                      {renderedColumns.map((col) => (
                        <Table.Cell
                          key={col.id}
                          className={cx('text-xs', col.align === 'right' && 'text-right', col.className)}
                        >
                          {col.cell(row)}
                        </Table.Cell>
                      ))}
                    </Table.Row>
                  )}
            </Table.Body>
          </Table>

          {totalPages > 1 && (
            <div className="p-4">
              <PaginationPageDefault page={pagination.page} total={totalPages} onPageChange={pagination.onPageChange} />
            </div>
          )}
        </div>
      </TableCard.Root>

      <ConfirmDialog
        open={pending !== null}
        onOpenChange={(open) => {
          if (!open && !isConfirming) setPending(null)
        }}
        title={pending?.confirm.title ?? ''}
        description={pending?.confirm.description ?? ''}
        confirmLabel={pending?.confirm.confirmLabel}
        cancelLabel={pending?.confirm.cancelLabel}
        destructive={pending?.confirm.destructive}
        isLoading={isConfirming}
        onConfirm={handleConfirm}
      />
    </div>
  )
}
